import { zodResolver } from '@hookform/resolvers/zod';
import { Button, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../components/drawer';
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
} from '../../../components/form/form';
import { DisabledWithTooltip } from '../../../components/permissions/DisabledWithTooltip';
import { cn } from '../../../components/utils';
import { useViewer } from '../../../context/viewer';

const NameFormSchema = z.object({
  name: z.string().trim().min(3, 'nameMinLength'),
});
export type NameEntityForm = z.infer<typeof NameFormSchema>;

export type NameEntity = { id: string; name: string };

// The underlying mutation's onError callback handles every error case (a
// specific toast for CONFLICT, a generic one otherwise) before mutateAsync's
// promise rejects, so by the time we get here the user has already been told
// what happened. Swallow rather than rethrow so it doesn't become an unhandled
// rejection that Datadog RUM reports as an uncaught error.
export async function runAndCloseOnSuccess(
  action: () => Promise<void>,
  onClose: () => void,
) {
  try {
    await action();
  } catch {
    return;
  }
  onClose();
}

export const NameEntityDrawer = ({
  entity,
  open,
  onClose,
  onSubmit,
  onDelete,
  createTitle,
  editTitle,
  entityLabel,
}: {
  entity?: NameEntity;
  open: boolean;
  onClose: () => void;
  onSubmit: (form: NameEntityForm, id?: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  createTitle: string;
  editTitle: string;
  /** Translated singular-plural entity label (e.g. t('bodyParts')) for permission tooltips. */
  entityLabel: string;
}) => {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const disableEdit = !hasPermissions('library.tags', 'edit');
  const disableDelete = !hasPermissions('library.tags', 'delete');
  const isEdit = !!entity;
  const title = isEdit ? editTitle : createTitle;

  const form = useForm<NameEntityForm>({
    resolver: zodResolver(NameFormSchema),
    defaultValues: { name: entity?.name ?? '' },
    mode: 'onChange',
  });
  const { handleSubmit, control } = form;

  useEffect(() => {
    if (open) {
      form.reset({ name: entity?.name ?? '' });
    }
  }, [open, entity, form]);

  const onSubmitHandler = (form: NameEntityForm) =>
    runAndCloseOnSuccess(
      () => onSubmit({ ...form, name: form.name.trim() }, entity?.id),
      onClose,
    );

  const onDeleteHandler = () => {
    // The delete button only renders when entity is set, so this is an
    // unreachable-in-practice guard rather than a real no-op path.
    if (!entity?.id) return Promise.resolve();
    return runAndCloseOnSuccess(() => onDelete(entity.id), onClose);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <DrawerHeader onClose={onClose}>
        <Typography variant="h2">{title}</Typography>
      </DrawerHeader>
      <Form {...form}>
        <form
          id="name-entity-form"
          onSubmit={handleSubmit(onSubmitHandler)}
          className="flex flex-col gap-2 flex-grow"
        >
          <div className="flex flex-col gap-2 grow p-6 pt-0">
            <FormField
              control={control}
              name="name"
              disabled={disableEdit}
              render={({ field }) => (
                <FormItem>
                  <TextField
                    label={t('name')}
                    {...field}
                    placeholder={t('enterName')}
                    fullWidth
                    autoFocus
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>
      <DrawerFooter
        className={cn(
          isEdit ? 'justify-between' : 'justify-end',
          'flex flex-row gap-2 w-full p-8',
        )}
      >
        {isEdit && (
          <DisabledWithTooltip
            disabled={disableDelete}
            reason={t('permissionDisabled.delete', { item: entityLabel })}
          >
            <Button variant="containedDestructive" onClick={onDeleteHandler}>
              {t('delete')}
            </Button>
          </DisabledWithTooltip>
        )}
        <div className="flex flex-row gap-2">
          <Button variant="outlined" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            form="name-entity-form"
            disabled={!form.formState.isValid || disableEdit}
          >
            {t('save')}
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};
